import client from "../client";
import { SaveXeroConfigRequest, TestXeroConnectionResult } from "./xeroServices";

// QuickBooks Online accounting-integration operations. These mirror the Xero /
// MYOB service functions — the AppSync accounting layer is provider-generic, so
// the response shapes reuse the shared AccountingConnection type and the
// reference data comes back in the same field-name dialect as Xero (accountId /
// taxType / trackingCategoryID), adapted server-side.

export type SaveQuickBooksConfigRequest = SaveXeroConfigRequest;
export type TestQuickBooksConnectionResult = TestXeroConnectionResult;

export interface SetQuickBooksBankAccountRequest {
  /** QBO Account Id of an existing Bank / Credit Card account (the paid-from account). */
  bank_account_accountId: string;
}

const CONNECTION_FIELDS = `
          organisation_id
          provider
          status
          is_authenticated
          tenant {
            id
            name
          }
          last_refreshed_at
          last_used_at
          updated_at
          disconnect {
            by
            reason
          }
          config {
            transactions_to_sync
            sync_assets_enabled
            default_bank_account {
              id
              name
              code
              description
            }
          }`;

export const connectQuickBooks = (callback_url: string) => {
  return client({
    query: `
      mutation ConnectQuickBooks($callback_url: String!) {
        connect_quickbooks(callback_url: $callback_url) {${CONNECTION_FIELDS}
        }
      }
    `,
    variables: { callback_url }
  });
};

export const disconnectQuickBooks = () => {
  return client({
    query: `
      mutation DisconnectQuickBooks {
        disconnect_quickbooks {${CONNECTION_FIELDS}
        }
      }
    `,
    variables: {}
  });
};

export const testQuickBooksConnection = () => {
  return client({
    query: `
      query TestQuickBooksConnection {
        test_quickbooks_connection {
          status
          last_tested_at
        }
      }
    `,
    variables: {}
  });
};

export const saveQuickBooksConfig = (data: SaveQuickBooksConfigRequest) => {
  return client({
    query: `
      mutation SaveQuickBooksConfig($input: SaveXeroConfigInput!) {
        save_quickbooks_config(input: $input) {${CONNECTION_FIELDS}
        }
      }
    `,
    variables: { input: data }
  });
};

export const setQuickBooksBankAccount = (data: SetQuickBooksBankAccountRequest) => {
  return client({
    query: `
      mutation SetQuickBooksBankAccount($input: SetQuickBooksBankAccountInput!) {
        set_quickbooks_bank_account(input: $input) {
          code
          message
        }
      }`,
    variables: { input: data }
  });
};

export const getOrganisationQuickBooksAccounts = () => {
  return client({
    query: `
      query GetOrganisationQuickBooksAccounts {
        get_organisation_quickbooks_accounts {
          accountId
          code
          name
          description
          taxType
          hidden
          split_transactions
          display_name
        }
      }`,
    variables: {}
  });
};

export const getOrganisationQuickBooksTaxCodes = () => {
  return client({
    query: `
      query GetOrganisationQuickBooksTaxCodes {
        get_organisation_quickbooks_tax_codes {
          taxType
          name
          hidden
        }
      }
    `,
    variables: {}
  });
};

export const getOrganisationQuickBooksCategories = () => {
  return client({
    query: `
      query GetOrganisationQuickBooksCategories {
        get_organisation_quickbooks_categories {
          trackingCategoryID
          name
          hidden
          display_name
          options {
            trackingOptionID
            name
            hidden
          }
        }
      }`,
    variables: {}
  });
};
